
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Helmet } from 'react-helmet-async';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';

const Profile = () => {
  const { user, profile, signOut, refreshProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t, tl } = useLanguage();
  
  // Try to refresh the profile once when the component loads if it's null
  useEffect(() => {
    if (user && !profile) {
      const timeoutId = setTimeout(() => {
        refreshProfile();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [user, profile, refreshProfile]);

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white text-black">
        <div className="container mx-auto py-12 px-4">
          <div className="text-center">{t('profile.pleaseSignIn')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white text-black">
      <Helmet>
        <title>{t('profile.title')} | Meubles Karim</title>
        <meta name="description" content={tl('Voir et gérer votre profil Meubles Karim', 'View and manage your Meubles Karim profile', 'Visualizza e gestisci il tuo profilo Meubles Karim')} />
      </Helmet>
      
      <div className="container mx-auto py-12 px-4">
        <h1 className="font-sans text-3xl font-medium mb-8 text-center">{t('profile.title')}</h1>
        
        <div className="max-w-xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>{t('profile.accountInformation')}</CardTitle>
            <CardDescription>{t('profile.accountInformationDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">{t('profile.email')}</h3>
              <p className="text-base">{profile?.email || user.email}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">{t('profile.accountType')}</h3>
              <p className="text-base">{profile?.is_admin || profile?.role === 'admin' ? t('profile.administrator') : t('profile.customer')}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-muted-foreground">{t('profile.memberSince')}</h3>
              <p className="text-base">{formatDate(profile?.created_at)}</p>
            </div>
            
            {!profile && (
              <div className="p-4 bg-amber-50 text-amber-800 rounded-md">
                <p>{t('profile.profileLoading')}</p>
              </div>
            )}
          </CardContent>
          <Separator />
          <CardFooter className="flex justify-between pt-6">
            <Button variant="outline" onClick={() => navigate('/estimates')}>
              {t('profile.viewMyEstimates')}
            </Button>
            <Button variant="destructive" onClick={handleSignOut} disabled={isLoading}>
              {isLoading ? t('profile.signingOut') : t('profile.signOut')}
            </Button>
          </CardFooter>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
